import { AnimatePresence, motion } from 'framer-motion';
import { Suspense, lazy, useState } from 'react';
import { Bookshelf } from './components/Bookshelf';
import { DebugOverlay } from './components/DebugOverlay';
import { ErrorBoundary } from './components/ErrorBoundary';
import { IPhone16Frame } from './components/IPhone16Frame';
import { NetworkStatusToast } from './components/NetworkStatusToast';
import type { NovelTheme } from './components/ReaderSettings';
import type { Novel } from './data/samples';
import { useAppThemeEffect } from './hooks/useAppThemeEffect';
import { getStorageItem } from './utils/storage';

const Reader = lazy(() => import('./components/Reader').then((m) => ({ default: m.Reader })));

function App() {
  const [selectedNovel, setSelectedNovel] = useState<Novel | null>(null);

  // テーマ設定を永続化
  const [currentTheme, setTheme] = useState<NovelTheme>(() => {
    return getStorageItem<NovelTheme>('novelflow_theme', 'paper');
  });

  useAppThemeEffect(currentTheme);

  return (
    <ErrorBoundary>
      <IPhone16Frame>
        <AnimatePresence mode="wait">
          {selectedNovel && (
            <motion.div
              key="reader"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-full relative z-10"
            >
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <div className="w-full h-full flex items-center justify-center font-serif text-sm opacity-50">
                      読込中…
                    </div>
                  }
                >
                  <Reader
                    novel={selectedNovel}
                    onBack={() => setSelectedNovel(null)}
                    currentTheme={currentTheme}
                    setTheme={setTheme}
                  />
                </Suspense>
              </ErrorBoundary>
            </motion.div>
          )}
          {!selectedNovel && (
            <motion.div
              key="bookshelf"
              initial={{ x: '-20%', opacity: 0.8 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-20%', opacity: 0.8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-full relative z-10"
            >
              <ErrorBoundary>
                <Bookshelf
                  onSelectNovel={setSelectedNovel}
                  currentTheme={currentTheme}
                  setTheme={setTheme}
                />
              </ErrorBoundary>
            </motion.div>
          )}
        </AnimatePresence>

        {/* オンライン/オフラインステータス通知トースト */}
        <NetworkStatusToast />

        {/* 開発環境用デバッグオーバーレイ */}
        <DebugOverlay />
      </IPhone16Frame>
    </ErrorBoundary>
  );
}

export default App;
